"use client";

import { useSession, useSupabaseClient } from "@/components/SupabaseProvider";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { deserializeFolder, type FolderModel } from "../models/folder_model";
import type { Database } from "../types/supabase";
import type { AutoPublishChange } from "./useAutoPublish";

export type AutoPublishProject = FolderModel & {
  taskStatus?: string;
  /** Only needed for failed source videos, which may still have usable shorts. */
  hasReadyShorts?: boolean;
  /** Scheduled posts remain queued until their status changes, even when overdue. */
  hasQueuedPosts?: boolean;
};

export const autoPublishProjectsQueryKey = (userId: string | undefined) =>
  ["auto-publish-projects", userId] as const;

/** Active projects first; recently used projects remain available after turning off. */
export function sortAutoPublishProjects(projects: readonly AutoPublishProject[]): AutoPublishProject[] {
  return [...projects].sort((a, b) =>
    Number(Boolean(b.autoPublish?.enabled)) - Number(Boolean(a.autoPublish?.enabled))
    || b.updatedAt.getTime() - a.updatedAt.getTime()
    || a.id.localeCompare(b.id),
  );
}

/** Read all owned projects, including ones that have never used Auto post. */
export const createFetchAutoPublishProjects = (
  supabase: Pick<SupabaseClient<Database>, "from">,
  userId: string | undefined,
) => async (signal?: AbortSignal): Promise<AutoPublishProject[]> => {
  if (!userId) throw new Error("User not found");
  const projects: AutoPublishProject[] = [];
  let cursor: string | undefined;
  for (;;) {
    let query = supabase.from("folders")
      .select("id, author_id, name, created_at, updated_at, thumbnail_url, task_id, auto_publish")
      .eq("author_id", userId)
      .order("id", { ascending: true })
      .limit(500);
    if (cursor) query = query.gt("id", cursor);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    if (error) throw error;
    if (!data?.length) break;
    projects.push(...data.map(deserializeFolder));
    cursor = data[data.length - 1].id;
  }

  // There is no folders -> tasks foreign key. Match the results page's
  // useTaskFromResource lookup by output_id, including older folders whose
  // task_id is absent, and keep both the URL size and response pages bounded.
  const projectsById = new Map(projects.map(project => [project.id, project]));
  for (let offset = 0; offset < projects.length; offset += 200) {
    let cursor: string | undefined;
    for (;;) {
      let query = supabase.from("tasks").select("id, output_id, status")
        .eq("author_id", userId)
        .in("output_id", projects.slice(offset, offset + 200).map(project => project.id))
        .order("id", { ascending: true }).limit(500);
      if (cursor) query = query.gt("id", cursor);
      if (signal) query = query.abortSignal(signal);
      const { data, error } = await query;
      if (error) throw error;
      if (!data?.length) break;
      for (const task of data) {
        const project = projectsById.get(task.output_id);
        if (project) project.taskStatus = task.status;
      }
      cursor = data[data.length - 1].id;
    }
  }

  const failedProjects = projects.filter(project => project.taskStatus === "error" || project.taskStatus === "expired");
  const failedIds = failedProjects.map(project => project.id);
  // A source can fail after producing shorts or bookings. Read only the two
  // identifiers needed to preserve that operational state, in bounded batches.
  const [readyFolders, queuedFolders] = await Promise.all([
    (async () => {
      const folders = new Set<string>();
      for (let offset = 0; offset < failedIds.length; offset += 200) {
        let cursor: string | undefined;
        for (;;) {
          let query = supabase.from("videos_v2").select("id, folder_id")
            .eq("author_id", userId).eq("status", "ready")
            .in("folder_id", failedIds.slice(offset, offset + 200))
            .order("id", { ascending: true }).limit(500);
          if (cursor) query = query.gt("id", cursor);
          if (signal) query = query.abortSignal(signal);
          const { data, error } = await query;
          if (error) throw error;
          if (!data?.length) break;
          for (const row of data) if (row.folder_id) folders.add(row.folder_id);
          cursor = data[data.length - 1].id;
        }
      }
      return folders;
    })(),
    (async () => {
      const folders = new Set<string>();
      for (let offset = 0; offset < failedIds.length; offset += 200) {
        let cursor: string | undefined;
        for (;;) {
          let query = supabase.from("publications").select("id, auto_folder_id")
            .eq("owner_id", userId).eq("status", "scheduled")
            .in("auto_folder_id", failedIds.slice(offset, offset + 200))
            .order("id", { ascending: true }).limit(500);
          if (cursor) query = query.gt("id", cursor);
          if (signal) query = query.abortSignal(signal);
          const { data, error } = await query;
          if (error) throw error;
          if (!data?.length) break;
          for (const row of data) if (row.auto_folder_id) folders.add(row.auto_folder_id);
          cursor = data[data.length - 1].id;
        }
      }
      return folders;
    })(),
  ]);
  for (const project of failedProjects) {
    project.hasReadyShorts = readyFolders.has(project.id);
    project.hasQueuedPosts = queuedFolders.has(project.id);
  }
  return sortAutoPublishProjects(projects);
};

/** Preserve other projects while reflecting a toggle or account edit immediately. */
export function applyAutoPublishProjectChange(
  projects: readonly AutoPublishProject[],
  folderId: string,
  change: Pick<AutoPublishChange, "enabled" | "accountIds">,
): AutoPublishProject[] {
  if (change.enabled === undefined && change.accountIds === undefined) return [...projects];
  return sortAutoPublishProjects(projects.map(project => project.id !== folderId ? project : {
    ...project,
    autoPublish: {
      enabled: change.enabled ?? project.autoPublish?.enabled ?? false,
      accountIds: change.accountIds ?? project.autoPublish?.accountIds ?? [],
    },
  }));
}

export function useAutoPublishProjects({ enabled = true }: { enabled?: boolean } = {}) {
  const supabase = useSupabaseClient();
  const userId = useSession()?.user.id;
  const active = enabled && Boolean(userId);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: autoPublishProjectsQueryKey(userId),
    queryFn: ({ signal }) => createFetchAutoPublishProjects(supabase, userId)(signal),
    enabled: active,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (!active) return;
    // Share the overview between Calendar and navigation, including source
    // failures and publication changes observed elsewhere in the app.
    return queryClient.getQueryCache().subscribe(event => {
      if (event.type !== "updated" ||
        !["publications", "task", "folder-videos"].includes(String(event.query.queryKey[0])) ||
        (event.action.type !== "invalidate" && event.action.type !== "success")) return;
      void queryClient.invalidateQueries({ queryKey: autoPublishProjectsQueryKey(userId) }, { cancelRefetch: false });
    });
  }, [active, queryClient, userId]);
  return {
    projects: active ? query.data ?? [] : [],
    hasLoaded: active && query.data !== undefined && query.data !== null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
